/**
 * Pages describe where the viewer is; the shell renders it. A page calls
 * useSetSpine({...}) once; FocusRail (in the top bar) and PageCrumb (top of
 * the content column) read the spec. Pages that never call it show no rail
 * and no crumb of ours — a rail is a statement about zoom, and only graduated
 * screens make it.
 *
 * A spec is plain, serialisable data on purpose: it survives JSON round-trips,
 * so it can never smuggle a component (or a closure over stale page state)
 * into the shell.
 */
import { createContext, useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { SpineLevel, SpineLockReason } from './spineLevels'

/** One piece of the "You're at" tail: plain text, or a bolded word with a
 * plain suffix — e.g. { b: 'Claims', text: 'lens' } renders "Claims lens". */
export type SpineTail = string | { b: string; text?: string }

/** One crumb segment. A bare string is the bold form the figures use for the
 * workspace and the current screen; `{ label, to }` is a link; `{ label,
 * plain: true }` is a muted step the viewer cannot click. */
export type SpineCrumb = string | { label: string; to?: string; plain?: boolean }

export interface SpineSpec {
  /** The level the viewer is at (the highlighted notch). */
  level: SpineLevel
  /** Notches to show, closest first. Defaults to useDefaultLevels() in the page. */
  levels: SpineLevel[]
  /** Where each notch goes. Missing → SPINE_DEFAULT_LINK → non-clickable. */
  links?: Partial<Record<SpineLevel, string>>
  /** The "You're at" tail after the bold level label, e.g. ['Meridian Ventures'] or
   * ['Aurora Battery Cathode', { b: 'Claims', text: 'lens' }, 'Claim 2 of 8']. */
  youreAt: SpineTail[]
  /** Steps waiting at THIS level → dot on the current notch + "N steps waiting" tail. */
  stepsWaiting?: number
  /** Other levels whose own banner has a step waiting — each gets the same dot,
   * so the viewer can see from here that something is pending out there. */
  pendingLevels?: SpineLevel[]
  /** Levels this viewer may see named but not open, and why. The reason is one
   * of the three in SPINE_LOCK_REASON and is shown on hover. */
  locked?: Partial<Record<SpineLevel, SpineLockReason>>
  /** Zoom controls. `null`/undefined renders the arrow disabled with the title. */
  zoomIn?: { to?: string; title: string } | null
  zoomOut?: { to?: string; title: string } | null
  /** The single crumb line under the top bar, e.g. ['Meridian Ventures', 'Portfolio']. */
  crumb?: SpineCrumb[]
}

interface SpineContextValue {
  spec: SpineSpec | null
  setSpec: (s: SpineSpec | null) => void
}

export const SpineContext = createContext<SpineContextValue>({ spec: null, setSpec: () => undefined })

export function SpineProvider({ children }: { children: ReactNode }) {
  const [spec, setSpecState] = useState<SpineSpec | null>(null)
  const setSpec = useCallback((s: SpineSpec | null) => setSpecState(s), [])
  const value = useMemo(() => ({ spec, setSpec }), [spec, setSpec])
  return <SpineContext.Provider value={value}>{children}</SpineContext.Provider>
}
